<template>
    <v-container class="counter-footer">
        <v-container v-if="!readOnly" class="pt-6" :class="{ bus: isBus, 'pt-12': isBus }">
            <div class="body-2 text-center ma-4 font-weight-medium">
                Dataene lagres automatisk underveis.
                <br />Trykk på Avslutt når turen er ferdig.
            </div>

            <v-alert
                v-if="validationMessage"
                type="error"
                variant="tonal"
                density="compact"
                class="mb-4 text-center"
                data-id="completion-validation-message"
            >
                {{ validationMessage }}
            </v-alert>

            <v-row>
                <v-col cols="6">
                    <v-dialog v-model="isCancelTripDialogVisible" persistent max-width="338">
                        <template #activator="{ props: itemProps }">
                            <v-btn large block class="text-none font-weight-bold" data-id="cancel-trip" v-bind="itemProps">
                                Kansellér tur
                            </v-btn>
                        </template>
                        <v-card>
                            <v-card-text class="pa-10">
                                <p class="text-center">
                                    <v-icon size="36"> mdi-alert-outline </v-icon>
                                </p>
                                <p class="body-1 font-weight-bold text-center">Er du sikker på at du vil kansellere turen?</p>
                                <v-row align="center">
                                    <v-col>
                                        <v-btn large block color="primary" class="text-none mb-4" data-id="ok" @click="cancelTrip">
                                            Ja
                                        </v-btn>
                                        <v-btn large block class="text-none" data-id="return" @click="isCancelTripDialogVisible = false">
                                            Tilbake
                                        </v-btn>
                                    </v-col>
                                </v-row>
                            </v-card-text>
                        </v-card>
                    </v-dialog>
                </v-col>
                <v-col cols="6">
                    <v-dialog v-model="isCompleteTripDialogVisible" persistent max-width="450" @update:return-value="reset()">
                        <template #activator="{ props: itemProps }">
                            <v-btn
                                large
                                block
                                color="primary"
                                class="text-none font-weight-regular"
                                data-id="end-trip"
                                :disabled="disableComplete"
                                v-bind="itemProps"
                                @click="buttonClick = true"
                            >
                                Avslutt hele turen
                            </v-btn>
                        </template>
                        <v-card>
                            <v-card-text class="pa-10">
                                <p class="text-center">
                                    <v-icon size="36"> mdi-alert-outline </v-icon>
                                </p>
                                <p class="body-1 font-weight-bold text-center">Er du sikker på at du vil avslutte turen?</p>
                                <v-row align="center">
                                    <v-col>
                                        <div v-if="tripHasInterchangeTripAndPassangers">
                                            <p class="body-1 font-weight-bold text-center">
                                                Utvekslingstur eksisterer:
                                                {{ interchangeTripId }}
                                            </p>
                                            <p class="body-1 font-weight-bold text-center">
                                                Antall sittende passasjerer:
                                                {{ interchangePassangers }}
                                            </p>
                                            <v-btn
                                                large
                                                block
                                                color="primary"
                                                class="text-none mb-4"
                                                data-id="interchange-trip"
                                                @click="interchangeTrip"
                                            >
                                                Ja, overfør passasjerer til neste tur
                                            </v-btn>
                                            <v-btn
                                                large
                                                block
                                                color="primary"
                                                class="text-none mb-4"
                                                data-id="complete-trip"
                                                @click="completeTrip"
                                            >
                                                Ja, ikke overfør passasjerer til neste tur
                                            </v-btn>
                                        </div>
                                        <div v-else>
                                            <v-btn large block color="primary" class="text-none mb-4" data-id="ok" @click="completeTrip">
                                                Ja
                                            </v-btn>
                                        </div>
                                        <v-btn large block class="text-none" data-id="return" @click="isCompleteTripDialogVisible = false">
                                            Tilbake
                                        </v-btn>
                                    </v-col>
                                </v-row>
                            </v-card-text>
                        </v-card>
                    </v-dialog>
                </v-col>
            </v-row>
        </v-container>
        <v-btn v-if="readOnly" block color="primary" large class="text-none" data-id="go-back" @click="goBack"> Tilbake </v-btn>
    </v-container>
</template>

<script lang="ts" setup>
import { ref, computed, nextTick } from 'vue';

const props = withDefaults(
    defineProps<{
        isBus: boolean;
        readOnly: boolean;
        interchangeTripId?: string;
        interchangePassangers: number;
        disableComplete?: boolean;
        validationMessage?: string | null;
    }>(),
    {
        isBus: false,
        readOnly: false,
        disableComplete: false
    }
);

const emit = defineEmits(['complete-trip', 'cancel-trip', 'go-back', 'interchange-trip']);

const buttonClick = ref(false);
const isCompleteTripDialogVisible = ref(false);
const isCancelTripDialogVisible = ref(false);

const tripHasInterchangeTripAndPassangers = computed(() => {
    return props.interchangeTripId && props.interchangePassangers > 0;
});

function reset() {
    buttonClick.value = false;
}

function completeTrip() {
    isCompleteTripDialogVisible.value = false;
    // to avoid vuetify bug with stuck screen overlay before navigating from page dialog should be closed
    // navigation in the next DOM update
    nextTick(() => emit('complete-trip', buttonClick));
}

function interchangeTrip() {
    isCompleteTripDialogVisible.value = false;
    nextTick(() => emit('interchange-trip', buttonClick));
}

function cancelTrip() {
    isCancelTripDialogVisible.value = false;
    nextTick(() => emit('cancel-trip'));
}

function goBack() {
    emit('go-back');
}

function showCompleteTripDialog() {
    if (props.disableComplete) return;
    isCompleteTripDialogVisible.value = true;
}

defineExpose({ showCompleteTripDialog });
</script>

<style lang="scss" scoped>
.bus {
    position: sticky;
    bottom: -38px;
    background: linear-gradient(rgba(255, 255, 255, 0), rgba(255, 255, 255, 1) 20%);
}
</style>
